import { IonButton, IonLoading, IonModal, IonToast } from "@ionic/react";
import React, { FC, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { setUsername } from "../redux/actions/user";
import { logoutAndRedirect } from "../services/authService";
import { trackEvent } from "../services/analytics/analyticsService";
import {
    transferSubscriptionFromReceipt,
    SubscriptionAccountMismatch,
} from "../services/subscriptionAccountMismatch";
import "./SubscriptionAccountMismatchAlert.css";

interface SubscriptionAccountMismatchAlertProps {
    mismatch: SubscriptionAccountMismatch | null;
    onDismiss: () => void;
}

type ToastColor = "success" | "warning" | "danger";
type ToastState = { message: string; color: ToastColor } | null;

// REFSIX-764 "we found two REFSIX accounts" modal. Shown when
// detectSubscriptionAccountMismatch finds the on-device receipt is bound to
// a DIFFERENT REFSIX account than the one the user is signed into. Renders
// the design's copy — the account the user is currently logged in as, then
// the other account's email, match count, last-match date and plan tier —
// and three actions:
//
//   1. "Transfer the <plan> subscription and matches to this account" —
//      moves subs AND copies the source user's match docs + GPS / heart-rate
//      fitness data over. Listed first per the design.
//   2. "Transfer the <plan> subscription to this account" — sub-only.
//   3. "Log out and use the other account" — pre-fills the login form with
//      the receipt's username and logs out (REFSIX-732 routing lands the
//      user on Login pre-filled).
//
// There is deliberately NO "Not now" and no backdrop dismiss (REFSIX-764:
// force the user to choose). A hidden black-on-black "×" in the top-right
// exists for test/QA dismissal only.
//
// Toast outcomes for the two Transfer paths:
//   - subs moved + matches included + matches ok → moveSuccessWithMatches
//   - subs moved + matches included + matches failed → moveSuccessPartial
//   - subs moved + matches not requested → moveSuccess
//   - 0 subs moved (benign no-op) → silent dismiss
//   - network/server failure → moveFailure (modal stays open for retry)
export const SubscriptionAccountMismatchAlert: FC<
    SubscriptionAccountMismatchAlertProps
> = ({ mismatch, onDismiss }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [isTransferring, setIsTransferring] = useState(false);
    const [toast, setToast] = useState<ToastState>(null);

    // Plan tier label: "pro" → "PRO", "basic" → "Plus". Falls back to the
    // PRO label if the tier is somehow unknown (a receipt-owning account
    // effectively always has an active paid plan, so this is defensive).
    const planLabel = mismatch
        ? mismatch.planFeatures === "basic"
            ? t("subscriptionMismatch.planPlus")
            : t("subscriptionMismatch.planPro")
        : "";

    // Interpolated strings can leave a double space when an optional token
    // (e.g. an empty plan label) is blank — collapse runs of whitespace so
    // the copy stays clean across every locale.
    const tidy = (value: string): string =>
        value.replace(/\s{2,}/g, " ").trim();

    // Email is the identifier the design shows; fall back to the username
    // when the customer doc has no email (usernames are typically emails).
    const accountIdentifier = mismatch
        ? mismatch.email || mismatch.receiptUsername
        : "";

    let bodyText = "";
    if (mismatch) {
        if (mismatch.matchCount > 0 && mismatch.lastMatchDate) {
            bodyText = t("subscriptionMismatch.body", {
                email: accountIdentifier,
                matchCount: mismatch.matchCount,
                lastMatch: moment(mismatch.lastMatchDate).fromNow(),
                plan: planLabel,
            });
        } else if (mismatch.matchCount > 0) {
            bodyText = t("subscriptionMismatch.bodyNoLastMatch", {
                email: accountIdentifier,
                matchCount: mismatch.matchCount,
                plan: planLabel,
            });
        } else {
            bodyText = t("subscriptionMismatch.bodyNoMatches", {
                email: accountIdentifier,
                plan: planLabel,
            });
        }
        bodyText = tidy(bodyText);
    }

    const handleMove = async (includeMatches: boolean): Promise<void> => {
        if (!mismatch || isTransferring) return;
        setIsTransferring(true);
        try {
            const result =
                await transferSubscriptionFromReceipt(includeMatches);
            // "Success" covers two states: this call moved subs
            // (transferredSubscriptions > 0), OR it cleaned up a prior
            // partial-failure (convergedFromRetry) — the user's plan is now
            // on the right account either way and should see a success toast.
            const succeeded =
                result &&
                (result.transferredSubscriptions > 0 ||
                    result.convergedFromRetry);
            if (succeeded) {
                let message: string;
                let color: ToastColor = "success";
                if (includeMatches) {
                    if (result.transferredMatches) {
                        message = t(
                            "subscriptionMismatch.moveSuccessWithMatches"
                        );
                    } else {
                        // Sub move succeeded, matches copy didn't. Don't
                        // celebrate — warning colour + honest string.
                        message = t("subscriptionMismatch.moveSuccessPartial");
                        color = "warning";
                    }
                } else {
                    message = t("subscriptionMismatch.moveSuccess");
                }
                setToast({ message, color });
                onDismiss();
            } else if (result) {
                // Server ok but zero moves (benign no-op) — dismiss quietly.
                onDismiss();
            } else {
                // Network/server failure — surface it and leave the modal
                // open so the user can retry or pick another option.
                setToast({
                    message: t("subscriptionMismatch.moveFailure"),
                    color: "danger",
                });
            }
        } finally {
            setIsTransferring(false);
        }
    };

    const handleMoveSubscription = () => handleMove(false);
    const handleMoveSubscriptionAndMatches = () => handleMove(true);

    const handleSwitchAccount = async () => {
        if (!mismatch) return;
        // Set the username BEFORE logout. The root reducer's USER_LOGOUT case
        // preserves the `user` slice, so the pre-filled value survives the
        // logout reset and the post-logout AuthStackScreen route picks it up.
        dispatch(setUsername(mismatch.receiptUsername));
        // Dismiss BEFORE awaiting logout. logoutAndRedirect flips
        // isAuthenticated, which unmounts this modal's subtree; dismissing
        // first lets the parent's once-per-session guard reset cleanly while
        // we're still mounted.
        onDismiss();
        try {
            await logoutAndRedirect();
        } catch (err) {
            console.warn(
                "SubscriptionAccountMismatchAlert switch failed",
                err
            );
        }
    };

    // Fire the "modal shown" analytics event once the modal is fully
    // presented (REFSIX-764 / REFSIX-683 tracking spec).
    const handleDidPresent = () => {
        if (!mismatch) return;
        trackEvent("SubscriptionMergeModalShown", {
            plan: mismatch.planFeatures,
            matchCount: mismatch.matchCount,
        });
    };

    return (
        <>
            {mismatch && (
                <IonModal
                    data-testid="modal-subscription-account-mismatch"
                    id="modal-subscription-account-mismatch"
                    className="subscription-mismatch-modal"
                    isOpen={!!mismatch}
                    // Force a choice (REFSIX-764). In Ionic 7
                    // backdropDismiss={false} blocks tap-outside AND the
                    // Esc key AND the Android hardware back button (the
                    // ionBackButton overlay handler only registers when
                    // the top overlay's backdropDismiss is truthy).
                    backdropDismiss={false}
                    onDidPresent={handleDidPresent}
                    // Belt-and-braces: if any framework path ever DOES
                    // dismiss the modal, clear the parent's mismatch state
                    // too — otherwise the modal closes visually but can
                    // never re-present (the parent still holds mismatch).
                    // Idempotent with the button paths, which call
                    // onDismiss directly before this fires on teardown.
                    onDidDismiss={onDismiss}
                >
                    <div className="subscription-mismatch-modal__content">
                        {/* Hidden test/QA dismiss — black × on black card. */}
                        <button
                            type="button"
                            aria-hidden="true"
                            tabIndex={-1}
                            data-testid="subscription-mismatch-hidden-dismiss"
                            className="subscription-mismatch-modal__hidden-dismiss"
                            onClick={onDismiss}
                        >
                            ×
                        </button>
                        <h2 className="subscription-mismatch-modal__title">
                            {t("subscriptionMismatch.title")}
                        </h2>
                        {/* Ticket copy opens with the CURRENT account so the
                            user can see both sides of the mismatch before
                            choosing. The session user_id is the login
                            identifier, which is an email address. */}
                        <p
                            className="subscription-mismatch-modal__body"
                            data-testid="subscription-mismatch-current-account"
                        >
                            {t("subscriptionMismatch.currentAccount", {
                                email: mismatch.currentUsername,
                            })}
                        </p>
                        <p
                            className="subscription-mismatch-modal__body"
                            data-testid="subscription-mismatch-body"
                        >
                            {bodyText}
                        </p>
                        <p className="subscription-mismatch-modal__prompt">
                            {t("subscriptionMismatch.prompt")}
                        </p>
                        <div className="subscription-mismatch-modal__buttons">
                            <IonButton
                                expand="block"
                                data-testid="subscription-mismatch-transfer-sub-and-matches"
                                onClick={handleMoveSubscriptionAndMatches}
                            >
                                {tidy(
                                    t(
                                        "subscriptionMismatch.transferSubscriptionAndMatches",
                                        { plan: planLabel }
                                    )
                                )}
                            </IonButton>
                            <IonButton
                                expand="block"
                                data-testid="subscription-mismatch-transfer-sub"
                                onClick={handleMoveSubscription}
                            >
                                {tidy(
                                    t(
                                        "subscriptionMismatch.transferSubscription",
                                        { plan: planLabel }
                                    )
                                )}
                            </IonButton>
                            {/* Same solid styling as the two transfer
                                buttons — UAT asked for all three to match
                                the design (no outline variant). */}
                            <IonButton
                                expand="block"
                                data-testid="subscription-mismatch-logout-use-other"
                                onClick={handleSwitchAccount}
                            >
                                {t("subscriptionMismatch.logoutUseOther")}
                            </IonButton>
                        </div>
                    </div>
                </IonModal>
            )}
            <IonLoading
                isOpen={isTransferring}
                message={t("subscriptionMismatch.moving")}
            />
            <IonToast
                isOpen={!!toast}
                message={toast?.message}
                color={toast?.color}
                duration={3000}
                onDidDismiss={() => setToast(null)}
            />
        </>
    );
};
